import React from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Tabs } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { Loader } from "@/components/ui/loader";
import { useAuthenticatedUser } from "@/hooks/use-authenticated-user";
import { Button } from "@/components/ui/button";
import { BusinessReport } from "./businessReports.types";
import { BusinessReportTemplateCard } from "./components/businessReportTemplateCard";
import { CreateBusinessReportModal } from "./modals/createModal";
import { BusinessReportsOutcomeList } from "./outcomeList";
import { useGetBusinessReportsQuery } from "./businessReports.api";
import { BusinessReportsTabs } from "@/modules/snapshots/components/businessReportsTabs";
import { SnapshotCollections } from "@/modules/snapshots/snapshotCollections";
import { useNavigate, useParams } from "react-router";

const emptyList: BusinessReport[] = [];

export const BusinessReportsView = () => {
  const navigate = useNavigate();

  const { tab = "templates" } = useParams<{ tab: string }>();
  const [selectedTab, setSelectedTab] = React.useState(tab);
  const handleChangeTab = (value: string) => {
    setSelectedTab(value);
    navigate(`/snapshots/${value}`);
  };
  const { data: businessReports = emptyList, isLoading } = useGetBusinessReportsQuery();
  const tableRef = React.useRef<HTMLDivElement>(null);
  const userData = useAuthenticatedUser();

  const [showCreateModal, setShowCreateModal] = React.useState(false);

  return isLoading ? (
    <Loader />
  ) : (
    <div className="container mx-auto px-4 py-6">
      <Tabs defaultValue={tab} className="w-full" onValueChange={handleChangeTab}>
        <BusinessReportsTabs selectedTab={selectedTab} />
      </Tabs>
      <div className="mt-1">
        <Separator />
      </div>
      {tab === "templates" && (
        <div className="mt-5 rounded-xl bg-white p-5 shadow-md">
          <h3 className="mb-5 text-2xl font-semibold">Select Template</h3>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            <AnimatePresence>
              {businessReports.map(businessReport => (
                <motion.div
                  key={businessReport.id}
                  layout
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.2 }}
                >
                  <BusinessReportTemplateCard businessReport={businessReport} />
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
          {userData.features.includes("business_report_admin") && (
            <div className="text-right">
              {showCreateModal && <CreateBusinessReportModal isOpen={showCreateModal} setIsOpen={setShowCreateModal} />}

              <Button variant="default" className="mt-5" onClick={() => setShowCreateModal(true)}>
                Create SnapShot template
              </Button>
            </div>
          )}
        </div>
      )}
      {tab === "recent" && (
        <div className="mt-5 rounded-xl bg-white p-5 shadow-md">
          <BusinessReportsOutcomeList tableRef={tableRef} />
        </div>
      )}
      {tab === "collections" && (
        <div className="mt-5 rounded-xl bg-white p-5 shadow-md">
          <SnapshotCollections />
        </div>
      )}
    </div>
  );
};
